/**
 * Panel de la pestaña "Mejoras": tienda de mejoras permanentes (Daño, Vida,
 * Defensa). Cada fila muestra nivel actual, bono acumulado y costo del
 * próximo nivel; el botón Comprar se deshabilita si no alcanzan las
 * monedas o ya está al máximo.
 */

import Phaser from 'phaser';
import type { MetaState, UpgradeDef } from '../../core/types';
import { upgrades as upgradeDefs } from '../../data';
import { canAffordUpgrade, getUpgradeCost, isUpgradeMaxed, purchaseUpgrade } from '../../meta';
import { PALETTE } from './palette';
import { UiButton, drawPanel, formatCompact } from './uiKit';

interface UpgradeRowHandle {
  def: UpgradeDef;
  levelText: Phaser.GameObjects.Text;
  bonusText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
  buyButton: UiButton;
}

export class UpgradesPanel extends Phaser.GameObjects.Container {
  private readonly rows: UpgradeRowHandle[] = [];
  private meta: MetaState;

  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    meta: MetaState,
    private readonly onPurchase: (nextMeta: MetaState) => void
  ) {
    super(scene, 0, 0);
    this.meta = meta;

    const title = scene.add.text(width / 2, 20, 'MEJORAS PERMANENTES', {
      fontFamily: 'monospace',
      fontSize: '15px',
      color: '#f4f0ff',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.add(title);

    const rowHeight = 96;
    const rowGap = 16;
    const startY = 56;
    const rowWidth = width - 32;

    upgradeDefs.forEach((def, i) => {
      const y = startY + i * (rowHeight + rowGap);
      const panel = drawPanel(scene, 16, y, rowWidth, rowHeight, PALETTE.panel2, PALETTE.border, 14);

      const nameText = scene.add.text(16 + 16, y + 14, def.name.toUpperCase(), {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#f4f0ff',
        fontStyle: 'bold',
      });

      const levelText = scene.add.text(16 + 16, y + 36, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#a99fd1',
      });

      const bonusText = scene.add.text(16 + 16, y + 54, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#6fcf6f',
      });

      const costText = scene.add.text(16 + 16, y + rowHeight - 20, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ffd166',
      });

      const buyButton = new UiButton(
        scene,
        16 + rowWidth - 62,
        y + rowHeight / 2,
        96,
        44,
        'COMPRAR',
        () => this.handlePurchase(def.id),
        { fill: PALETTE.accent, fillHover: PALETTE.accentSoft, textColor: '#241c3d', fontSize: '12px' }
      );

      this.add([panel, nameText, levelText, bonusText, costText, buyButton]);
      this.rows.push({ def, levelText, bonusText, costText, buyButton });
    });

    this.setSize(width, height);
    scene.add.existing(this);
    this.refresh(meta);
  }

  private handlePurchase(id: UpgradeDef['id']): void {
    if (!canAffordUpgrade(this.meta, id)) return;
    const next = purchaseUpgrade(this.meta, id);
    this.onPurchase(next);
  }

  /** Redibuja niveles/costos/estado de botones tras cualquier cambio de meta. */
  refresh(meta: MetaState): void {
    this.meta = meta;
    for (const row of this.rows) {
      const level = meta.upgrades[row.def.id];
      const maxed = isUpgradeMaxed(row.def.id, level);
      row.levelText.setText(`Nivel ${level} / ${row.def.maxLevel}`);
      row.bonusText.setText(`Bono acumulado: +${level * row.def.valuePerLevel}`);
      if (maxed) {
        row.costText.setText('¡Nivel máximo alcanzado!');
        row.buyButton.setText('MÁXIMO').setEnabled(false);
      } else {
        const cost = getUpgradeCost(row.def.id, level);
        row.costText.setText(`Costo: ${formatCompact(cost)} monedas`);
        row.buyButton.setText('COMPRAR').setEnabled(canAffordUpgrade(meta, row.def.id));
      }
    }
  }
}
